import React from 'react';

import {styled} from '@netx/core-components/Theme';

import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {
	VirtualizedListArrowTabTrapProvider,
	useVirtualizedListArrowTabTrapProvider,
} from '@netx/core-components/VirtualizedList';

import VirtualizedPermissionsList, {
	type VirtualizedPermissionsListProps,
} from '../VirtualizedList';
import VirtualizedPermissionListItem, {
	type VirtualizedPermissionListItemFocusTrapDataProps,
} from './VirtualizedPermissionListItemFocusTrap';

export type PermissionsProps = VirtualizedPermissionsListProps & {
	height?: number;
	addedAtIndex?: number;
};

const VirtualizedListTrapFocusRoot = styled('div', {
	name: 'VirtualizedListTrapFocus',
	slot: 'Root',
})(({theme}) => ({
	outline: 'none',
	display: 'flex',
	flexDirection: 'column',
	minHeight: 56,
	width: '100%',
	flex: '1 1 auto',
	overflow: 'hidden',
	'& ul': {
		margin: 0,
	},
}));

export const PermissionsPrimitive = React.forwardRef<
	HTMLDivElement,
	PermissionsProps
>(({height, virtualListRef, addedAtIndex = -1, ...props}, ref) => {
	const itemProvider = useVirtualizedListArrowTabTrapProvider();

	const internalVirtualListRef = useSharedRef(null, [virtualListRef]);
	const rootRef = useSharedRef<HTMLDivElement | null>(null, [
		ref,
		itemProvider.rootRef,
	]);

	return (
		// We are a "tab" trap of sorts
		// If a user tabs to this area arrow keys are now in play
		// to navigate through the contents
		<VirtualizedListTrapFocusRoot
			onFocusCapture={itemProvider.onFocusCapture}
			onBlur={itemProvider.onBlur}
			tabIndex={0}
			ref={rootRef}
			aria-activedescendant={itemProvider.activeDescendant}
			role="menu"
		>
			<VirtualizedPermissionsList<VirtualizedPermissionListItemFocusTrapDataProps>
				{...props}
				innerElementType="ul"
				virtualListRef={internalVirtualListRef}
				onScrollIndexUpdate={itemProvider.onScrollIndexUpdate}
				ListItemComponent={VirtualizedPermissionListItem}
				ListItemComponentProps={{
					...props.ListItemComponentProps,
					activeIndex: itemProvider.state.activeIndex,
					focusTrapActive: itemProvider.state.active,
					tabIndex: -1,
					onInteracting: itemProvider.onItemInteracting,
					onClick: itemProvider.onItemClick,
				}}
			/>
		</VirtualizedListTrapFocusRoot>
	);
});

export const Permissions = React.memo(
	React.forwardRef<HTMLDivElement, PermissionsProps>(
		({virtualListRef, ...props}, ref) => {
			const internalVirtualListRef = useSharedRef(null, [virtualListRef]);
			return (
				<VirtualizedListArrowTabTrapProvider
					addedAtIndex={props.addedAtIndex}
					activeDescendantLabel={'permission'}
					virtualListRef={internalVirtualListRef}
					total={props.total}
					pageSize={props.pageSize}
				>
					<PermissionsPrimitive
						{...props}
						virtualListRef={internalVirtualListRef}
						ref={ref}
					/>
				</VirtualizedListArrowTabTrapProvider>
			);
		},
	),
);

export default Permissions;
